import { BaseViewModel } from "@iot-platform/core";
import type { NotificationAlertUpdate } from "@station/domain/models/notification-alert.model.ts";
import {
    NotificationAlert,
    NotificationAlerts,
} from "@station/domain/models/notification-alert.model.ts";
import { NotificationAlertDeleteUseCase } from "@station/domain/usecases/notification-alert-delete.usecase.ts";
import { NotificationAlertGetAllUseCase } from "@station/domain/usecases/notification-alert-get-all.usecase.ts";
import { NotificationAlertUpdateUseCase } from "@station/domain/usecases/notification-alert-update.usecase.ts";
import { inject, injectable } from "inversify";
import { computed, makeObservable, observable, runInAction } from "mobx";

export const MAX_NOTIFICATION_ALERTS = 7;

@injectable()
export class NotificationViewmodel extends BaseViewModel {
    @observable
    showMore = false;

    @observable
    private stationId?: string;

    @observable
    notificationAlerts: NotificationAlerts = NotificationAlerts.empty();

    @observable
    initialLoading = false;

    @computed
    get filteredNotifications(): readonly Readonly<NotificationAlert>[] {
        return this.showMore
            ? this.notificationAlerts.notificationAlerts
            : this.notificationAlerts.notificationAlerts.slice(
                  0,
                  MAX_NOTIFICATION_ALERTS,
              );
    }

    constructor(
        @inject(NotificationAlertUpdateUseCase)
        private readonly notificationAlertUpdateUseCase: NotificationAlertUpdateUseCase,
        @inject(NotificationAlertDeleteUseCase)
        private readonly notificationAlertDeleteUseCase: NotificationAlertDeleteUseCase,
        @inject(NotificationAlertGetAllUseCase)
        private readonly notificationAlertGetAllUseCase: NotificationAlertGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    private async initViewData(): Promise<void> {
        await this.getAllNotificationAlerts();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    async updateNotificationAlert(
        notificationAlertUpdate: NotificationAlertUpdate,
    ): Promise<void> {
        await this.notificationAlertUpdateUseCase.execute(
            notificationAlertUpdate,
        );
        this.notificationAlerts = NotificationAlerts.empty();

        await this.getAllNotificationAlerts();
    }

    private async getAllNotificationAlerts(): Promise<void> {
        if (this.stationId) {
            const notificationFetched =
                await this.notificationAlertGetAllUseCase.execute(
                    this.stationId,
                );
            runInAction(() => {
                this.notificationAlerts = notificationFetched;
            });
        }
    }

    async deleteNotificationAlert(notificationId: string): Promise<void> {
        await this.notificationAlertDeleteUseCase.execute(notificationId);
        await this.getAllNotificationAlerts();
    }

    toggleShowMore(): void {
        runInAction(() => {
            this.showMore = !this.showMore;
        });
    }

    setStationId(stationId: string): void {
        runInAction(() => {
            this.stationId = stationId;
        });
    }
}
